import { logSwallowed } from '@/lib/errors';
import { createLogger } from '@/lib/logger';
import {
  cacheGet,
  cacheSet,
  cacheMerge,
  cacheGetById,
  cacheClear,
  cacheClearAll,
  type Row,
} from '@/lib/storage/persistedCache';

const log = createLogger('LocalCache');

const DEFAULT_TTL_MS = 5 * 60 * 1000; // 5 minutes

// In-memory hot cache for synchronous reads
const memoryCache = new Map<string, Row[]>();
const subscribers = new Map<string, Set<(data: Row[]) => void>>();
let hydrated = false;
let hydrationPromise: Promise<void> | null = null;

function key(schema: string, table: string): string {
  return `${schema}:${table}`;
}

function notify(schema: string, table: string, data: Row[]): void {
  const k = key(schema, table);
  const subs = subscribers.get(k);
  if (subs) {
    for (const cb of subs) {
      try {
        cb(data);
      } catch (err) {
        log.error('Subscriber error:', err);
      }
    }
  }
}

async function persistToStorage(schema: string, table: string, data: Row[]): Promise<void> {
  try {
    await cacheSet(schema, table, data, DEFAULT_TTL_MS);
  } catch (err) {
    log.error('Failed to persist cache:', err);
  }
}

/**
 * Hydrate memory cache from persisted IndexedDB storage.
 * Called once at app startup.
 */
export async function hydrate(): Promise<void> {
  if (hydrated) return;
  if (hydrationPromise) return hydrationPromise;

  hydrationPromise = (async () => {
    try {
      const keys = await cacheKeys();
      for (const k of keys) {
        const match = k.match(/^cache:([^:]+):(.+)$/);
        if (!match) continue;
        const schema = match[1];
        const table = match[2];
        const data = await cacheGet<Row>(schema, table);
        if (data) {
          memoryCache.set(key(schema, table), data);
          notify(schema, table, data);
        }
      }
      hydrated = true;
      log.info('LocalCache hydrated from persisted storage');
    } catch (err) {
      log.error('Hydration failed:', err);
    }
  })();

  return hydrationPromise;
}

export function isHydrated(): boolean {
  return hydrated;
}

export function get<T extends Row>(schema: string, table: string): T[] | undefined {
  return memoryCache.get(key(schema, table)) as T[] | undefined;
}

export function set<T extends Row>(schema: string, table: string, data: T[]): void {
  memoryCache.set(key(schema, table), data);
  notify(schema, table, data);
  // Persist asynchronously in background
  persistToStorage(schema, table, data as Row[]).catch((e) => logSwallowed(e, 'lib/localCache.ts:92'));
}

export function merge<T extends Row>(schema: string, table: string, rows: T[]): void {
  const k = key(schema, table);
  const existing = (memoryCache.get(k) as T[] | undefined) ?? [];
  const byId = new Map<string, T>();

  for (const row of existing) {
    if (row.id) byId.set(row.id, row);
  }
  for (const row of rows) {
    if (row.id) byId.set(row.id, row);
  }

  const merged = Array.from(byId.values());
  set(schema, table, merged);
}

export function getById<T extends Row>(schema: string, table: string, id: string): T | undefined {
  const rows = get<T>(schema, table);
  if (!rows) return undefined;
  return rows.find(r => r.id === id);
}

export function insert<T extends Row>(schema: string, table: string, row: T): void {
  const rows = get<T>(schema, table) ?? [];
  rows.push(row);
  set(schema, table, rows);
}

export function update<T extends Row>(schema: string, table: string, id: string, updates: Partial<T>): void {
  const rows = get<T>(schema, table) ?? [];
  const idx = rows.findIndex(r => r.id === id);
  if (idx >= 0) {
    rows[idx] = { ...rows[idx], ...updates };
    set(schema, table, rows);
  }
}

export function remove(schema: string, table: string, id: string): void {
  const rows = get<Row>(schema, table);
  if (!rows) return;
  const filtered = rows.filter(r => r.id !== id);
  set(schema, table, filtered);
}

export function clear(schema: string, table: string): void {
  memoryCache.delete(key(schema, table));
  notify(schema, table, []);
  cacheClear(schema, table).catch((e) => logSwallowed(e, 'lib/localCache.ts:142'));
}

export function clearAll(): void {
  memoryCache.clear();
  subscribers.clear();
  cacheClearAll().catch((e) => logSwallowed(e, 'lib/localCache.ts:148'));
}

export function subscribe(
  schema: string,
  table: string,
  callback: (data: Row[]) => void
): () => void {
  const k = key(schema, table);
  let subs = subscribers.get(k);
  if (!subs) {
    subs = new Set();
    subscribers.set(k, subs);
  }
  subs.add(callback);

  // Immediately notify with current data if available
  const hot = memoryCache.get(k);
  if (hot) {
    try {
      callback(hot);
    } catch (err) {
      log.error('Initial subscriber error:', err);
    }
  }

  return () => {
    subs?.delete(callback);
  };
}

export const localCache = {
  hydrate,
  isHydrated,
  get,
  set,
  merge,
  getById,
  insert,
  update,
  remove,
  clear,
  clearAll,
  subscribe,
};

async function cacheKeys(): Promise<string[]> {
  const { idbKeysByPrefix } = await import('./storage/idb');
  return idbKeysByPrefix('cache:');
}
